import { useState, MouseEvent, FC } from 'react';
import { useDispatch } from 'react-redux';

import classNames from 'classnames';
import { Trash2, Pencil, Download } from 'lucide-react';
import { BaseMenu, BaseMenuItem } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { BetCoefficientTypesEnum, BetOutcomeResultTypesEnum } from '@shared/constants';
import { textStyles } from '@shared/styles';
import { BetOutcomeModel } from '@shared/types';

import { useLazyExportOutcomeBettorsQuery } from '@services';

import {
  AppDispatch,
  setIsBetOutcomeDeleteModalOpened,
  setIsBetOutcomeEditModalOpened,
  setProcessingBetOutcome,
} from '@store';

import { BetSystemEventOutcomeProps } from './types';

import styles from './BetSystemEventOutcome.module.css';

const {
  actionsContainer,
  betEventOutcomeItem,
  pendingOutcomeResultType,
  positiveOutcomeResultType,
  outcomeTextContainer,
  negativeOutcomeResultType,
  coefficientTypeManual,
  coefficientTypeAuto,
} = styles;

const { defaultText } = textStyles;

export const BetSystemEventOutcome: FC<BetSystemEventOutcomeProps> = ({ betEventOutcome, betEventName }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { name, coefficientType, resultType } = betEventOutcome;

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isMenuOpened = Boolean(menuAnchor);

  const [exportOutcomeBettors] = useLazyExportOutcomeBettorsQuery();

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleBetOutcomeEditModalOpen = (betEventOutcome: BetOutcomeModel) => {
    dispatch(setProcessingBetOutcome(betEventOutcome));

    dispatch(setIsBetOutcomeEditModalOpened(true));
  };

  const handleBetOutcomeDeleteModalOpen = (betEventOutcome: BetOutcomeModel) => {
    dispatch(setProcessingBetOutcome(betEventOutcome));

    dispatch(setIsBetOutcomeDeleteModalOpened(true));
  };

  const menuItems: BaseMenuItem[] = [
    {
      icon: <Pencil />,
      text: 'Edit',
      onClick: () => handleBetOutcomeEditModalOpen(betEventOutcome),
    },
    {
      icon: <Download />,
      text: 'Export',
      onClick: () =>
        exportOutcomeBettors({
          betOutcomeId: betEventOutcome.id,
          betEventName,
        }),
    },
    {
      icon: <Trash2 />,
      text: 'Delete',
      onClick: () => handleBetOutcomeDeleteModalOpen(betEventOutcome),
    },
  ];

  return (
    <Box className={betEventOutcomeItem}>
      <Typography className={defaultText}>Name: {name}</Typography>
      <Typography className={classNames(defaultText, outcomeTextContainer)}>
        <Typography className={defaultText} marginRight="5px">
          Coefficient Type:
        </Typography>
        <Typography
          className={classNames(defaultText, {
            [coefficientTypeManual]: coefficientType === BetCoefficientTypesEnum.MANUAL,
            [coefficientTypeAuto]: coefficientType === BetCoefficientTypesEnum.AUTO,
          })}
        >
          {coefficientType}
        </Typography>
      </Typography>
      <Typography className={classNames(defaultText, outcomeTextContainer)}>
        <Typography className={defaultText} marginRight="5px">
          Result Type:
        </Typography>
        <Typography
          className={classNames(defaultText, {
            [pendingOutcomeResultType]: resultType === BetOutcomeResultTypesEnum.PENDING,
            [positiveOutcomeResultType]: resultType === BetOutcomeResultTypesEnum.POSITIVE,
            [negativeOutcomeResultType]: resultType === BetOutcomeResultTypesEnum.NEGATIVE,
          })}
        >
          {resultType}
        </Typography>
      </Typography>
      <Box className={actionsContainer}>
        <Typography className={defaultText}>Settings:</Typography>
        <BaseMenu
          menuItems={menuItems}
          menuAnchor={menuAnchor}
          isMenuOpened={isMenuOpened}
          handleOpenMenu={handleOpenMenu}
          handleCloseMenu={handleCloseMenu}
        />
      </Box>
    </Box>
  );
};
